/**
 * DocumentTypeMappingsSection — F18 (FK).
 *
 * Mapowanie TYPU dokumentu (nasz typ `document_types` → kod typu Symfonii FK) dla jednej
 * integracji. To NIE mapowanie pól (`FieldMappingsSection` — rubryki w środku), tylko
 * mapowanie CAŁEGO rodzaju dokumentu. Warunek eksportu dokumentów do księgowości FK.
 *
 * Zakres: tylko klasy faktur (sprzedaż/zakup) — pozostałe klasy nie księgują się w FK.
 * GET zwraca zapisane mapowania + nasze typy faktur firmy + katalog podpowiedzi Symfonii.
 * Klient wybiera per typ kod FK (albo „nie mapuj") → „Zapisz" = bulk replace POST.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { RefreshCw, Save } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface OurDocumentType {
  id: string;
  class: string;
  code: string;
  name: string;
  /** `global` = typ systemowy (katalog platformy), `local` = własny firmy. */
  origin?: 'local' | 'global';
}

interface SymfoniaType {
  code: string;
  label: string;
  group: 'sale' | 'purchase';
  kind: string;
}

interface MappingRow {
  id?: string;
  document_type_id: string;
  symfonia_type_code: string;
}

interface MappingsResponse {
  mappings: MappingRow[];
  documentTypes: OurDocumentType[];
  symfoniaTypes: SymfoniaType[];
}

/** Grupa dokumentu FK wyliczona przez backend z pola `Character`. */
type FkDocumentGroup = 'purchase' | 'sale' | 'other' | 'unknown';

/**
 * Typ dokumentu FK pobrany NA ŻYWO. Kształt potwierdzony na serwerze testowym:
 * `{ Id, Code, Name, YearId, Character }` — wyświetlamy `Code` (np. FVS) + `Name`.
 * `group` dokłada backend (zakup/sprzedaż/inne z `Character`) → filtr dropdowna mapowania.
 * `Symbol`/indeks zostawione defensywnie na wypadek innej wersji WebAPI.
 */
interface FkLiveType {
  Code?: string;
  Symbol?: string;
  Name?: string;
  Character?: number;
  group?: FkDocumentGroup;
  [key: string]: unknown;
}

/** Rok obrachunkowy FK (slim) do wyboru w UI. `closed`: 0 = otwarty. */
interface FkYearOption {
  id: number;
  code: string;
  closed: number;
}

interface FkLiveTypesResponse {
  years: FkYearOption[];
  yearId: number | null;
  yearCode: string | null;
  types: FkLiveType[];
}

const liveTypeCode = (tp: FkLiveType): string => tp.Code ?? tp.Symbol ?? '';
const liveTypeLabel = (tp: FkLiveType): string => tp.Name ?? tp.Code ?? tp.Symbol ?? '—';

export function DocumentTypeMappingsSection({
  integrationId,
  erpSystem,
  connectionMode,
}: {
  integrationId: string;
  erpSystem?: string;
  connectionMode?: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Podgląd żywych typów z FK ma sens tylko dla FK w trybie bezpośrednim (endpoint tego wymaga).
  const canFetchLive = erpSystem === 'symfonia_fk' && connectionMode === 'direct';

  const dataQ = useQuery({
    queryKey: ['erp-document-type-mappings', integrationId],
    queryFn: async () => {
      const res = await api.get<MappingsResponse>(
        `/erp-integrations/${integrationId}/document-type-mappings`,
      );
      return res.data;
    },
  });

  // Wybrany rok obrachunkowy dla żywych typów (`null` = otwarty rok, decyduje backend).
  // W FK typy są PER ROK, więc pozwalamy przełączyć rok w panelu.
  const [liveYearId, setLiveYearId] = useState<number | null>(null);
  // Panel raz aktywowany (kliknięto „Pobierz typy z FK") → zmiana roku auto-przeładowuje.
  const [liveActivated, setLiveActivated] = useState(false);

  // Dla FK w trybie bezpośrednim pobieramy żywe typy OD RAZU — dropdown mapowania jedzie na
  // realnych kodach z FK (nie na katalogu statycznym). Gdy FK padnie, `liveQ` zgłosi błąd, a
  // dropdown zdegraduje do katalogu (patrz `optionsFor`). Nie blokujemy ekranu.
  useEffect(() => {
    if (canFetchLive) setLiveActivated(true);
  }, [canFetchLive]);

  // Żywe typy z FK — leniwie, tylko na klik (FK bywa wolne/offline; nie blokujemy ekranu).
  // Po aktywacji zmiana roku (`liveYearId` w kluczu) sama przeładowuje typy.
  const liveQ = useQuery({
    queryKey: ['erp-fk-document-types', integrationId, liveYearId],
    queryFn: async () => {
      const res = await api.get<FkLiveTypesResponse>(
        `/erp-integrations/${integrationId}/fk-document-types`,
        liveYearId !== null ? { params: { yearId: liveYearId } } : undefined,
      );
      return res.data;
    },
    enabled: liveActivated,
    retry: false,
    gcTime: 0,
  });

  // Wybór kodu FK per nasz typ dokumentu: documentTypeId -> code ('' = nie mapuj).
  const [selection, setSelection] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!dataQ.data) return;
    const init: Record<string, string> = {};
    for (const m of dataQ.data.mappings) init[m.document_type_id] = m.symfonia_type_code;
    setSelection(init);
  }, [dataQ.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const mappings = Object.entries(selection)
        .filter(([, code]) => code)
        .map(([document_type_id, symfonia_type_code]) => ({
          document_type_id,
          symfonia_type_code,
        }));
      const res = await api.post(`/erp-integrations/${integrationId}/document-type-mappings`, {
        mappings,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.settings.integrations.docTypeMappings.saved'),
      });
      queryClient.invalidateQueries({ queryKey: ['erp-document-type-mappings', integrationId] });
    },
    onError: (err) => {
      const message = isAxiosError(err)
        ? (err.response?.data?.message as string | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title: message ?? t('tenant.settings.integrations.docTypeMappings.saveError'),
      });
    },
  });

  if (dataQ.isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  const data = dataQ.data;
  if (!data) return null;

  const liveErrorMessage = isAxiosError(liveQ.error)
    ? ((liveQ.error.response?.data?.message as string | undefined) ?? liveQ.error.message)
    : liveQ.error instanceof Error
      ? liveQ.error.message
      : '';

  // Panel „żywe typy z FK" (tylko FK w trybie bezpośrednim). Pokazujemy go TAKŻE, gdy firma
  // nie ma jeszcze naszych typów faktur — podgląd typów z księgowości jest wtedy przydatny
  // (wiadomo, co w ogóle w FK jest, zanim założysz i zmapujesz własne typy).
  const livePanel = canFetchLive ? (
    <div className="rounded-lg border border-border-secondary bg-bg-secondary p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-text-tertiary">
          {t('tenant.settings.integrations.docTypeMappings.live.hint')}
        </p>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<RefreshCw className="h-4 w-4" />}
          loading={liveQ.isFetching}
          onClick={() => (liveActivated ? liveQ.refetch() : setLiveActivated(true))}
          className="shrink-0"
        >
          {liveQ.data
            ? t('tenant.settings.integrations.docTypeMappings.live.refresh')
            : t('tenant.settings.integrations.docTypeMappings.live.button')}
        </Button>
      </div>

      {/* Wybór roku obrachunkowego — pojawia się dopiero, gdy znamy listę lat z FK.
          Typy dokumentów w FK są PER ROK, więc przełączenie roku przeładowuje listę. */}
      {liveQ.data && liveQ.data.years.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <label className="text-xs text-text-tertiary" htmlFor="fk-doc-types-year">
            {t('tenant.settings.integrations.docTypeMappings.live.yearLabel')}
          </label>
          <Dropdown
            id="fk-doc-types-year"
            className="w-40"
            aria-label={t('tenant.settings.integrations.docTypeMappings.live.yearLabel')}
            value={String(liveQ.data.yearId ?? '')}
            onChange={(value) => setLiveYearId(value === '' ? null : Number(value))}
            options={liveQ.data.years.map((y) => ({
              value: String(y.id),
              label:
                y.closed === 0
                  ? t('tenant.settings.integrations.docTypeMappings.live.yearOpen', {
                      year: y.code,
                    })
                  : y.code,
            }))}
          />
        </div>
      )}

      {liveQ.isError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {t('tenant.settings.integrations.docTypeMappings.live.error', {
            message: liveErrorMessage,
          })}
        </p>
      )}

      {liveQ.data &&
        (liveQ.data.types.length === 0 ? (
          <p className="mt-2 text-xs text-text-secondary">
            {t('tenant.settings.integrations.docTypeMappings.live.empty')}
          </p>
        ) : (
          <div className="mt-2">
            <p className="text-xs font-medium text-text-primary">
              {liveQ.data.yearCode
                ? t('tenant.settings.integrations.docTypeMappings.live.title', {
                    year: liveQ.data.yearCode,
                  })
                : t('tenant.settings.integrations.docTypeMappings.live.titleNoYear')}
            </p>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {liveQ.data.types.map((tp, i) => (
                <li
                  key={liveTypeCode(tp) || i}
                  className="rounded border border-border-secondary bg-bg-primary px-2 py-1 text-xs text-text-secondary"
                >
                  <span className="font-mono font-medium text-text-primary">
                    {liveTypeCode(tp) || '?'}
                  </span>
                  <span className="ml-1.5">{liveTypeLabel(tp)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  ) : null;

  if (data.documentTypes.length === 0) {
    return (
      <div className="space-y-4">
        <p className="rounded-lg border border-dashed border-border-secondary p-4 text-sm text-text-secondary">
          {t('tenant.settings.integrations.docTypeMappings.noTypes')}
        </p>
        {livePanel}
      </div>
    );
  }

  const classLabel = (cls: string) =>
    cls === 'sale_invoice'
      ? t('tenant.settings.integrations.docTypeMappings.class.sale_invoice')
      : t('tenant.settings.integrations.docTypeMappings.class.purchase_invoice');

  // Czy dropdown mapowania jedzie na ŻYWYCH kodach z FK. Warunek: FK bezpośredni + udany pull +
  // niepusta lista. Gdy FK offline/pusto → degradacja do katalogu statycznego (`data.symfoniaTypes`).
  const liveTypes = liveQ.data?.types ?? [];
  const usingLiveCodes = canFetchLive && !liveQ.isError && liveTypes.length > 0;
  // Fallback jawny tylko gdy PRÓBOWALIŚMY (FK bezpośredni) i się nie udało — nie strasz przy Handlu.
  const usingStaticFallback =
    canFetchLive && (liveQ.isError || (!liveQ.isFetching && liveTypes.length === 0));

  const optionsFor = (cls: string) => {
    const group: 'sale' | 'purchase' = cls === 'sale_invoice' ? 'sale' : 'purchase';
    const noneOption = { value: '', label: t('tenant.settings.integrations.docTypeMappings.none') };

    const built = usingLiveCodes
      ? liveTypes
          // `unknown` (nowy typ FK spoza znanego zestawu) pokazujemy w obu grupach — bezpieczny
          // fallback, żeby nowy typ FK nie zniknął z listy wyboru. `other` (nie-faktura) — nigdzie.
          .filter((tp) => tp.group === group || tp.group === 'unknown')
          .map((tp) => ({
            value: liveTypeCode(tp),
            label: `${liveTypeLabel(tp)} (${liveTypeCode(tp)})`,
          }))
          .filter((o) => o.value)
      : data.symfoniaTypes
          .filter((s) => s.group === group)
          .map((s) => ({ value: s.code, label: `${s.label} (${s.code})` }));

    return [noneOption, ...built];
  };

  // Zapisany kod, którego nie ma na bieżącej liście (np. z innego roku / stary katalog) — dołóż
  // go jawnie do opcji danego wiersza, żeby zapisane mapowanie nie „znikło" po zmianie roku.
  const optionsWithSaved = (cls: string, savedCode: string) => {
    const opts = optionsFor(cls);
    if (savedCode && !opts.some((o) => o.value === savedCode)) {
      opts.push({
        value: savedCode,
        label: t('tenant.settings.integrations.docTypeMappings.savedCodeMissing', {
          code: savedCode,
        }),
      });
    }
    return opts;
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-text-primary">
          {t('tenant.settings.integrations.docTypeMappings.title')}
        </h4>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {t('tenant.settings.integrations.docTypeMappings.hint')}
        </p>
        {usingStaticFallback && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            {t('tenant.settings.integrations.docTypeMappings.staticFallback')}
          </p>
        )}
      </div>

      {/* Jedno wąskie obramowanie z cienkimi separatorami (gęstość jak w mapowaniu pól
          kontrahentów/pracowników) — bez grubych kart per wiersz. */}
      <div className="divide-y divide-border-secondary overflow-hidden rounded-lg border border-border-secondary">
        {data.documentTypes.map((dt) => {
          return (
            <div key={dt.id} data-testid={`doc-type-mapping-${dt.id}`}>
              {/* Dokumenty = TYLKO mapowanie typów (nasz typ → typ FK). Mapowanie pól usunięte. */}
              <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center">
                <div className="text-sm text-text-primary sm:flex-1">
                  <span className="font-medium">{dt.name}</span>
                  <span className="ml-2 text-xs text-text-tertiary">
                    {classLabel(dt.class)} · {dt.code}
                  </span>
                  {dt.origin === 'global' && (
                    <span className="ml-2 rounded bg-bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
                      {t('tenant.settings.integrations.docTypeMappings.systemType')}
                    </span>
                  )}
                </div>
                <div className="min-w-0 sm:w-1/2">
                  <SearchableSelect
                    data-testid={`doc-type-fk-code-${dt.id}`}
                    options={optionsWithSaved(dt.class, selection[dt.id] ?? '')}
                    value={selection[dt.id] ?? ''}
                    onChange={(value) => setSelection((s) => ({ ...s, [dt.id]: value ?? '' }))}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          size="sm"
          leftIcon={<Save className="h-4 w-4" />}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('tenant.settings.integrations.docTypeMappings.save')}
        </Button>
      </div>

      {livePanel}
    </div>
  );
}
